/*
  Cart reservation — Lily & June (byte copy of Sableau's live assets/cart-reservation.js, 25-10-2026;
  operator: drawer 1:1 like Sableau). Sableau-specific notes below (Lasso/Whop, Revolut door) are kept as they are;
  on LJ the checkout is Shopify's own, and the handoff guard covers it the same way.

  Cart reservation — Sableau
  ---------------------------------------------------------------------------
  "Your cart is reserved for 10:00" is a promise, so this script keeps it: when
  the clock runs out the cart is actually emptied through /cart/clear.js. A
  countdown that lapses and leaves everything sitting there is the kind of claim
  the store was already penalised for once (Shopify merchant trust, 02-09-2026);
  either the words are true or they do not go on the page.

  Where the bar appears
    - the Essential cart drawer, as a full-width band under the drawer header.
      That drawer belongs to an app and takes no Liquid, so it is hooked through
      the same data-essential-cart-element attributes
      assets/drawer-free-shipping.js and assets/cart-savings.js already use;
    - the cart page and the theme's own drawer, as a contained notice above the
      checkout button.
    All of it is built here rather than half in Liquid, so there is one piece of
    markup to keep right instead of two that can drift.

  What resets the clock
    Every cart mutation (add, change, update) — an active shopper is never
    punished for taking his time over sizes. At zero, a shopper who tapped,
    typed or scrolled during the hold gets a new hold (20-10). Only an idle
    cart expires.

  What the clock never takes away
    A shopper who has already clicked through to checkout. The Lasso/Whop
    checkout lives on another origin, so this tab keeps counting while he is
    typing his card number; clearing the cart under him would be the one
    genuinely expensive bug in here. The click stamps a handoff time and the
    reservation is extended for as long as that stamp is fresh.

  What expiry does NOT do
    Lose the order. The line items are copied to localStorage before the cart is
    cleared, and the expired bar offers "Restore my cart", which puts them back
    in one request. The reservation is real, the loss is not permanent.

  Times are absolute (epoch ms in localStorage), so two open tabs agree and a
  backgrounded tab cannot drift. Every storage read is wrapped: Safari private
  mode throws on access, and the correct answer there is "no reservation", not a
  broken cart page.

  Checked by store/sableau/tools/test-cart-reservation.mjs (run it with node).
*/
(function () {
  'use strict';
  // Multilingual (14-10): visible strings from window.SB_T (snippets/sb-i18n.liquid), English fallback.
  function T(k, f) { return (window.SB_T && window.SB_T[k]) || f; }

  if (window.__sbCartReservationLoaded) return;
  window.__sbCartReservationLoaded = true;

  var CONFIG = {};
  try {
    var node = document.getElementById('sb-cart-reservation-config');
    if (node) CONFIG = JSON.parse(node.textContent || '{}');
  } catch (e) { /* guards below fall back to the defaults */ }

  /* minutes: 0 in the theme settings is the off switch — no bar, no clearing.
     A missing or unreadable config falls back to the 10 minutes the copy
     promises rather than to "off", so a broken settings block cannot quietly
     leave the claim on the page with nothing behind it. */
  var MINUTES = CONFIG.minutes === undefined || CONFIG.minutes === null || CONFIG.minutes === ''
    ? 10
    : Number(CONFIG.minutes);
  if (!(MINUTES > 0)) return;
  var HOLD_MS = MINUTES * 60 * 1000;
  /* How long a checkout click keeps the cart alive. Whop's own payment session
     runs an hour; 30 minutes covers a card retry and a 3DS detour without
     holding a truly abandoned cart forever. */
  var HANDOFF_GRACE_MS = 30 * 60 * 1000;
  var KEY = 'sableau:cart-reservation';
  var HANDOFF_KEY = 'sableau:checkout-handoff';
  var MARK = 'sb-reservation';

  var ANCHORS = [
    /* Essential cart drawer (app markup, no Liquid): a full-width band straight
       under the drawer header. It sat above the checkout button until 10-09 and
       ended up rendered twice, because assets/drawer-free-shipping.js inserts
       its savings block against that same anchor and pushed the bar away from
       it. The header is the first thing the shopper reads and nothing else
       writes there. */
    { selector: '[data-essential-cart-element="cart-drawer-header"]', position: 'after', variant: 'band' },
    // Cart page and the theme's own drawer, above the checkout button.
    { selector: '.cart__ctas', position: 'before', variant: 'inline' },
    /* An emptied cart page drops the whole summary block, .cart__ctas with it
       (sections/main-cart.liquid wraps it in `unless cart.empty?`). That is
       exactly the screen the shopper lands on straight after an expiry, so the
       "Continue shopping" button carries the notice and the restore link there. */
    { selector: '.cart-items__empty-button', position: 'before', variant: 'inline' }
  ];

  // ---------------------------------------------------------------- storage

  function read(key) {
    try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch (e) { return null; }
  }

  function write(key, value) {
    try {
      if (value === null) localStorage.removeItem(key);
      else localStorage.setItem(key, JSON.stringify(value));
    } catch (e) { /* private mode: the countdown still runs for this page view */ }
  }

  // ------------------------------------------------------------------ state

  var state = {
    cart: null,        // last /cart.js payload
    expiresAt: 0,      // epoch ms, 0 = no reservation
    expired: false,    // cleared by us, waiting for the shopper to restore
    saved: null,       // line items snapshot for "Restore my cart"
    notice: '',        // why the last restore attempt put nothing back
    clearing: false
  };

  /* Authoritative: localStorage is the source of truth across tabs and across
     the reload that follows an expiry, so anything not in storage is not true
     any more either. Leaving stale in-memory values here is how a second tab
     ends up counting down a reservation the first tab already ended. */
  function loadState() {
    var stored = read(KEY);
    state.expired = false;
    state.saved = null;
    state.expiresAt = 0;
    if (!stored) return;
    if (stored.expired) {
      state.expired = true;
      state.saved = stored.saved || null;
      return;
    }
    // A reservation belongs to the cart it was started for. After a different
    // cart is loaded (new session, cleared elsewhere) the old deadline is void.
    if (stored.token && state.cart && stored.token !== state.cart.token) return;
    if (typeof stored.expiresAt === 'number') state.expiresAt = stored.expiresAt;
  }

  function saveState() {
    if (state.expired) {
      write(KEY, { expired: true, saved: state.saved });
      return;
    }
    if (!state.expiresAt) { write(KEY, null); return; }
    write(KEY, { expiresAt: state.expiresAt, token: state.cart && state.cart.token });
  }

  function startFresh() {
    state.expiresAt = Date.now() + HOLD_MS;
    state.expired = false;
    state.saved = null;
    state.notice = '';
    saveState();
  }

  /* Any tap, key or scroll counts as activity, in any tab (stored, throttled to one write per 30 s). Only a cart nobody
     touched for a whole hold expires. */
  var ACTIVE_KEY = 'sableau:cart-active';
  var lastNoted = 0;
  function noteActivity() {
    var now = Date.now();
    if (now - lastNoted < 30000) return;
    lastNoted = now;
    write(ACTIVE_KEY, { at: now });
  }
  function recentlyActive() {
    var stamp = read(ACTIVE_KEY);
    return !!(stamp && typeof stamp.at === 'number' && Date.now() - stamp.at < HOLD_MS);
  }

  function handoffIsFresh() {
    var stamp = read(HANDOFF_KEY);
    return !!(stamp && typeof stamp.at === 'number' && Date.now() - stamp.at < HANDOFF_GRACE_MS);
  }

  // ------------------------------------------------------------------- cart

  function getCart() {
    return fetch('/cart.js', { headers: { accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  /* Keep only what /cart/add.js accepts back. Line-item properties survive so a
     restored cart is the same cart, not an approximation of it. */
  function snapshot(cart) {
    if (!cart || !cart.items) return null;
    var items = cart.items.map(function (i) {
      var out = { id: i.variant_id, quantity: i.quantity };
      if (i.properties && Object.keys(i.properties).length) out.properties = i.properties;
      if (i.selling_plan_allocation && i.selling_plan_allocation.selling_plan) {
        out.selling_plan = i.selling_plan_allocation.selling_plan.id;
      }
      return out;
    });
    return items.length ? items : null;
  }

  function expire() {
    if (state.clearing) return;
    if (handoffIsFresh()) {
      // Mid-payment on the external checkout: hold, do not clear.
      state.expiresAt = Date.now() + HOLD_MS;
      saveState();
      render();
      return;
    }
    if (recentlyActive()) {
      // 20-10 (code review #6): still browsing (reviews, size guide) without touching the cart: a new hold, not an empty cart.
      startFresh();
      render();
      return;
    }
    if (!state.cart || !state.cart.item_count) { reset(); return; }

    state.clearing = true;
    state.saved = snapshot(state.cart);
    fetch('/cart/clear.js', {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (r) { if (!r.ok) throw new Error('clear ' + r.status); return r.json(); })
      .then(function (cart) {
        state.expired = true;
        state.expiresAt = 0;
        state.cart = cart || { item_count: 0, items: [] };
        saveState();
        render();
        // Let the theme and the drawer app redraw their own totals. These fire
        // synchronously into our own listener, so the clearing flag stays up
        // until they have passed.
        document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
        state.clearing = false;
        if (isCartSurface()) window.location.reload();
      })
      .catch(function () {
        // Could not clear: do not claim it happened. Give the shopper the time back.
        state.clearing = false;
        state.saved = null;
        startFresh();
        render();
      });
  }

  function addItems(items) {
    /* snippets/klaviyo-tracking.liquid: a restore is no "Added to Cart". The flag is cleared after the response;
       clarity-events.js (which sends 'sb:cart-add') sees that response first, its handler wraps fetch. */
    var off = function () { if (typeof window !== 'undefined') window.__sbRestoring = false; };
    if (typeof window !== 'undefined') window.__sbRestoring = true;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: items })
    }).then(function (r) { off(); return r; }, function (e) { off(); throw e; });
  }

  function restore() {
    if (!state.saved || !state.saved.length) return;
    /* Own copy: every add that succeeds echoes through patchFetch into sync(), and startFresh() there
       empties state.saved while this loop is still walking it. */
    var saved = state.saved.slice();
    addItems(saved)
      .then(function (r) {
        if (r.ok) return saved.length;
        if (r.status < 400 || r.status >= 500) throw new Error('add ' + r.status);
        /* 4xx (422 sold out, 404 variant gone) = one line is refused, and Shopify then refuses the whole batch. Put them back one by one so
           a single sold-out size does not cost the shopper the rest of his cart. */
        var added = 0;
        return saved.reduce(function (chain, item) {
          return chain
            .then(function () { return addItems([item]); })
            .then(function (one) { if (one.ok) added++; }, function () {});
        }, Promise.resolve()).then(function () { return added; });
      })
      .then(function (added) {
        if (!added) throw new Error('nothing restored');
        /* ponytail: a line that stayed sold out is dropped without a word here — the reload shows the cart
           that did come back. Name the missing item once shoppers actually ask where it went. */
        state.expired = false;
        state.saved = null;
        state.notice = '';
        write(KEY, null);
        window.location.reload();
      })
      .catch(function () {
        // Nothing went back in: keep the saved cart and the bar, and say so instead of doing nothing.
        state.notice = T('res_fail', 'We could not restore your cart just now. Please try again.') + ' ';
        render();
      });
  }

  function reset() {
    state.expiresAt = 0;
    state.expired = false;
    state.saved = null;
    write(KEY, null);
    render();
  }

  // ------------------------------------------------------------------- view

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  /* Both halves padded: "09:48" keeps the same width every second, so the line
     around it does not twitch as the clock runs down. */
  function clockText(ms) {
    var total = Math.max(0, Math.ceil(ms / 1000));
    var m = Math.floor(total / 60);
    var s = total % 60;
    return (m < 10 ? '0' + m : m) + ':' + (s < 10 ? '0' + s : s);
  }

  function mount(anchor, spec) {
    var bar = el('div', MARK);
    /* role="timer", not "status": a status region is aria-live, so every one-second tick of the clock
       re-announced "Your cart is reserved for …" — the repeated line reported 29-09-2026. A timer region
       is not announced on each change; the text is still read when the shopper reaches it. */
    bar.setAttribute('role', 'timer');
    bar.__variant = spec.variant;

    var body = el('p', MARK + '__text');
    var action = el('button', MARK + '__restore', T('res_restore', 'Restore my cart'));
    action.type = 'button';
    action.addEventListener('click', restore);

    bar.appendChild(el('span', MARK + '__dot'));
    bar.appendChild(body);
    bar.__clock = el('b', MARK + '__clock');
    bar.__body = body;
    bar.__action = action;

    anchor.parentNode.insertBefore(bar, spec.position === 'after' ? anchor.nextSibling : anchor);
    /* Remember the bar on the anchor itself rather than looking at siblings.
       Sibling-hunting is what produced the duplicate: another script inserted a
       block between the bar and its anchor, the next pass no longer recognised
       it, and built a second one. */
    anchor.__sbReservationBar = bar;
    return bar;
  }

  /* 20-10 (code review #9): the digits change through the existing text node (a characterData change). Setting
     textContent swapped the node every second, a childList change that woke all six page-wide MutationObservers
     (theme, apps, Shopify) once a second on every page for as long as the cart held anything. */
  function setClock(clock, text) {
    var node = clock.firstChild;
    if (node && node.nodeType === 3 && !node.nextSibling) { if (node.nodeValue !== text) node.nodeValue = text; }
    else if (clock.textContent !== text) clock.textContent = text;
  }

  function paint(bar) {
    var body = bar.__body;
    var base = MARK + ' ' + MARK + '--' + bar.__variant;
    /* Rebuild only when the message changes. render() answers every DOM mutation, and a rebuild is a mutation:
       until 19-10 the bar rebuilt itself every frame (~60/s, measured) as long as the cart held anything, and woke
       every other MutationObserver on the page each time. The clock digits are updated in place instead. */
    var key = state.expired ? 'expired|' + (state.notice || '') + '|' + (state.saved && state.saved.length ? 1 : 0) : 'reserved';
    if (bar.__key === key && body.firstChild) {
      if (!state.expired) {
        var text = clockText(state.expiresAt - Date.now());
        setClock(bar.__clock, text);
      }
      return;
    }
    bar.__key = key;
    body.textContent = '';

    if (state.expired) {
      bar.className = base + ' ' + MARK + '--expired';
      body.appendChild(document.createTextNode(T('res_expired', 'Your reservation expired, so the cart was released.') + ' '));
      if (state.notice) body.appendChild(document.createTextNode(state.notice));
      if (state.saved && state.saved.length) body.appendChild(bar.__action);
      return;
    }

    bar.className = base;
    bar.__clock.textContent = clockText(state.expiresAt - Date.now());
    body.appendChild(document.createTextNode(T('res_reserved', 'Your cart is reserved for') + ' '));
    body.appendChild(bar.__clock);
  }

  function isCartSurface() {
    return /^(\/[a-z]{2}(-[a-z]{2})?)?\/cart(\/|$)/i.test(window.location.pathname);   // also /de/cart, /nb/cart (14-10)
  }

  /* The bars placed by the last render(). tick() uses this to repaint only the digits instead of walking the
     whole document once a second — see tick(). */
  var mounted = [];

  function render() {
    var show = state.expired || (state.expiresAt > 0 && state.cart && state.cart.item_count > 0);
    var live = [];

    if (show) {
      for (var i = 0; i < ANCHORS.length; i++) {
        var spec = ANCHORS[i];
        /* On /cart the page itself carries the notice; a second copy in the (closed) drawer is what made
           "Your cart is reserved for" read twice in the page text (review 29-09). */
        if (spec.variant === 'band' && isCartSurface() &&
            document.querySelectorAll('.cart__ctas, .cart-items__empty-button').length) continue;
        /* One bar per surface. While the drawer app re-renders, the same header can briefly exist twice
           (the outgoing copy and the new one); mounting on every match stacked two "reserved for" lines.
           Take the first visible match, or the first match when none is laid out yet. */
        var anchors = document.querySelectorAll(spec.selector);
        var anchor = null;
        /* offsetParent forces a layout pass; with a single match the answer is that match either way (19-10). */
        if (anchors.length > 1) for (var j = 0; j < anchors.length && !anchor; j++) if (anchors[j].offsetParent !== null) anchor = anchors[j];
        if (!anchor) anchor = anchors[0];
        if (!anchor) continue;
        var bar = anchor.__sbReservationBar;
        if (!bar || !bar.isConnected) bar = mount(anchor, spec);
        paint(bar);
        live.push(bar);
      }
    }

    /* Sweep. Anything carrying our class that is not one of the bars just
       placed is a leftover — an anchor the drawer app replaced, or a bar that
       drifted away from its anchor. One pass here is cheaper than trusting
       every insertion point to clean up after itself. */
    var all = document.querySelectorAll('.' + MARK);
    for (var k = 0; k < all.length; k++) {
      if (live.indexOf(all[k]) === -1) all[k].remove();
    }
    mounted = live;
  }

  // ------------------------------------------------------------------ clock

  /* 20-10 (review #2): a shopper who switches to TikTok and comes back after 10+ minutes had her cart emptied by the
     first tick, before she could tap anything; a reload gives a fresh hold instead. Coming back now counts as
     activity: a visible tab again, or a tick after a long gap (the in-app browser froze its timers). */
  var lastTick = Date.now();
  function welcomeBack() { lastNoted = 0; noteActivity(); }
  function tick() {
    var now = Date.now(), slept = now - lastTick > 5000;
    lastTick = now;
    if (document.hidden) return;
    if (slept) welcomeBack();
    if (state.expired || !state.expiresAt) return;
    if (Date.now() >= state.expiresAt) { expire(); return; }
    /* Only the digits change between two ticks, so repaint the digits. This used to call render(), which runs
       four querySelectorAll passes over the whole document — every second, on every page of the store, for a
       shop whose traffic is 94.8% mobile. Nothing can appear on screen without a DOM mutation, and the
       MutationObserver in start() already answers those with a render(), so an empty `mounted` means there is
       genuinely nothing to paint. */
    if (!mounted.length) return;
    var text = clockText(state.expiresAt - Date.now());
    for (var i = 0; i < mounted.length; i++) {
      var bar = mounted[i];
      if (!bar.isConnected) { render(); return; }   // the drawer app replaced our anchor: rebuild
      if (bar.__clock) setClock(bar.__clock, text);
    }
  }

  // --------------------------------------------------------------- plumbing

  /* One place learns that the cart changed: every mutation on this storefront
     goes through fetch (the theme and the drawer app both use it), so patching
     it once beats guessing at each surface's own events. The theme's cart events
     are listened to as well, in case a future surface posts a form instead. */
  var MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  function patchFetch() {
    var original = window.fetch;
    if (typeof original !== 'function') return;
    window.fetch = function (input) {
      var url = '';
      try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (e) {}
      var isMutation = MUTATION.test(url);
      return original.apply(this, arguments).then(function (response) {
        // 20-10 (code review #12): guarded, so this hook can never turn a good response into a rejected fetch.
        try { if (isMutation && response.ok && !state.clearing) sync(true); } catch (e) {}
        return response;
      });
    };
  }

  /* Any click that heads for checkout — the theme's button, the Lasso-patched
     one, the drawer app's, or the second door — stamps the handoff so expire()
     knows the shopper is on the payment page rather than idle.
     .cart__alt-checkout-link is the second door ("Pay via Revolut instead"). It is
     deliberately a <button> with no /checkout href so the Lasso script leaves it
     alone, which also meant it matched none of the selectors below: a shopper who
     took that door and had a second tab open lost his cart ten minutes into typing
     his card. Added 12-09-2026. */
  function watchCheckout() {
    document.addEventListener('pointerdown', function (event) {
      var t = event.target;
      if (!t || !t.closest) return;
      if (t.closest('[name="checkout"], #checkout, a[href*="/checkout"], .cart__alt-checkout-link, [data-essential-cart-element="checkout-button-wrapper"]')) {
        write(HANDOFF_KEY, { at: Date.now() });
      }
    }, true);
  }

  function sync(isMutation) {
    return getCart().then(function (cart) {
      state.cart = cart;

      if (isMutation) {
        if (!cart || !cart.item_count) {
          /* Two very different events look identical here: the shopper removed
             his last line, or we just cleared the cart ourselves and the
             /cart/clear.js call echoed back through the cart events. Storage is
             what tells them apart — treating the echo as "shopper emptied it"
             wipes the expired notice and the restore link with it. */
          loadState();
          if (state.expired) render();
          else reset();
          return;
        }
        // A real change: the shopper is active, so he gets the full hold again.
        startFresh();
        render();
        return;
      }

      /* Read storage first. The page that expires a cart reloads itself, and
         the reload lands here with an empty cart: deciding before this line
         would throw away the expired notice, and with it the shopper's only
         route back to "Restore my cart". */
      loadState();

      if (!cart || !cart.item_count) {
        if (!state.expired) reset();
        else render();
        return;
      }

      // Items are back (restored, or a fresh add in another tab): the expired
      // notice is stale, so the cart gets a full reservation again.
      if (state.expired || !state.expiresAt || state.expiresAt <= Date.now()) startFresh();
      render();
    });
  }

  function start() {
    patchFetch();
    watchCheckout();
    ['pointerdown', 'keydown', 'scroll'].forEach(function (name) {
      window.addEventListener(name, noteActivity, { passive: true, capture: true });
    });

    sync(false);

    setInterval(tick, 1000);
    document.addEventListener('visibilitychange', function () { if (!document.hidden) welcomeBack(); });
    window.addEventListener('pageshow', function (e) { if (e.persisted) welcomeBack(); });

    // The drawer app rebuilds its footer on every open; re-mount into it.
    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; try { render(); } catch (e) {} });
    }).observe(document.body, { childList: true, subtree: true });

    ['cart:update', 'cart:updated', 'cart:refresh', 'cart:change'].forEach(function (name) {
      document.addEventListener(name, function () { if (!state.clearing) sync(true); });
    });

    // A second tab that expired the cart must not leave this one counting down.
    window.addEventListener('storage', function (e) {
      if (e.key === KEY) { loadState(); sync(false); }
    });
  }

  // Test seam for store/sableau/tools/test-cart-reservation.mjs. Nothing in the
  // storefront reads it; it exists so the rules that cost money if they break —
  // the handoff hold and the restore snapshot — can be run outside a browser.
  window.__sbCartReservation = {
    state: state,
    clockText: clockText,
    snapshot: snapshot,
    render: render,
    handoffIsFresh: handoffIsFresh,
    expire: expire,
    restore: restore,
    tick: tick,
    sync: sync,
    start: start,
    KEY: KEY,
    HANDOFF_KEY: HANDOFF_KEY
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
